class PieceFeaturedProducts extends HTMLElement {
  /** @type {number | null} */
  #layoutFrame = null;

  /** @type {Array<() => void>} */
  #cleanups = [];

  /** @type {Set<() => void>} */
  #layoutSchedulers = new Set();

  /** @type {Set<string>} */
  static preloadedImageUrls = new Set();

  /** @type {Set<string>} */
  static prefetchedDocumentUrls = new Set();

  connectedCallback() {
    this.initCarousels();
    this.initSwatches();
    this.initViewAllPrefetch();
  }

  disconnectedCallback() {
    if (this.#layoutFrame != null) {
      cancelAnimationFrame(this.#layoutFrame);
      this.#layoutFrame = null;
    }

    this.#cleanups.forEach((cleanup) => cleanup());
    this.#cleanups = [];
    this.#layoutSchedulers.clear();
  }

  scheduleLayouts() {
    if (this.#layoutFrame != null) return;

    this.#layoutFrame = requestAnimationFrame(() => {
      this.#layoutFrame = null;
      this.#layoutSchedulers.forEach((runLayout) => runLayout());
    });
  }

  initViewAllPrefetch() {
    const link = this.querySelector('.piece-fp__view-all[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const prefetch = () => {
      PieceFeaturedProducts.prefetchDocument(link.href);
    };

    link.addEventListener('pointerenter', prefetch, { once: true, passive: true });
    link.addEventListener('focus', prefetch, { once: true });
  }

  initCarousels() {
    this.querySelectorAll('[data-fp-carousel]').forEach((carousel) => {
      const viewport = carousel.querySelector('[data-fp-viewport]');
      if (!viewport) return;

      const prevBtn = carousel.querySelector('[data-fp-prev]');
      const nextBtn = carousel.querySelector('[data-fp-next]');
      const gap = this.readCardGap();

      const scrollAmount = () => {
        if (this.dataset.layout === 'carousel') {
          const columns = this.readColumnCount();
          const firstCard = viewport.querySelector('[data-fp-card]');
          if (!firstCard) return viewport.clientWidth;
          return (firstCard.offsetWidth + gap) * columns;
        }

        const firstCard = viewport.querySelector('[data-fp-card]');
        return firstCard ? firstCard.offsetWidth + gap : 300;
      };

      const updateNavVisibility = () => {
        const maxScroll = viewport.scrollWidth - viewport.clientWidth;
        const canScroll = maxScroll > 1;

        if (prevBtn) {
          prevBtn.toggleAttribute('hidden', !canScroll);
          prevBtn.disabled = !canScroll || viewport.scrollLeft <= 1;
        }
        if (nextBtn) {
          nextBtn.toggleAttribute('hidden', !canScroll);
          nextBtn.disabled = !canScroll || viewport.scrollLeft >= maxScroll - 1;
        }
      };

      const positionNav = () => {
        const grid = viewport.querySelector('.piece-fp__grid');
        if (!grid) return;

        const cards = [...grid.querySelectorAll('[data-fp-card]')].filter(
          (card) => card.offsetParent !== null
        );
        if (!cards.length) return;

        const rowCount = Number.parseInt(this.dataset.rowCount, 10) || 1;
        const columns = this.readColumnCount();
        const carouselRect = carousel.getBoundingClientRect();
        const isCarouselLayout = this.dataset.layout === 'carousel';

        /** @type {number | null} */
        let topEdge = null;
        /** @type {number | null} */
        let bottomEdge = null;

        const cardRowEdges = (/** @type {Element[]} */ rowCards) => {
          if (!rowCards.length) return null;

          const rects = rowCards.map((card) => card.getBoundingClientRect());
          return {
            top: Math.min(...rects.map((rect) => rect.top)),
            bottom: Math.max(...rects.map((rect) => rect.bottom)),
          };
        };

        const getGridRowCards = (/** @type {number} */ rowIndex) => {
          const rowCards = [];
          for (let col = 0; col < columns; col += 1) {
            const cardIndex = col * rowCount + rowIndex;
            if (cards[cardIndex]) rowCards.push(cards[cardIndex]);
          }
          return rowCards;
        };

        if (isCarouselLayout && rowCount === 2) {
          const firstRow = cardRowEdges(getGridRowCards(0));
          const secondRow = cardRowEdges(getGridRowCards(1));
          if (firstRow && secondRow) {
            topEdge = firstRow.top;
            bottomEdge = secondRow.bottom;
          }
        } else {
          const singleRow = cardRowEdges(
            isCarouselLayout ? getGridRowCards(0) : cards.slice(0, columns)
          );
          if (singleRow) {
            topEdge = singleRow.top;
            bottomEdge = singleRow.bottom;
          }
        }

        if (topEdge == null || bottomEdge == null) return;

        const centerY = topEdge + (bottomEdge - topEdge) / 2 - carouselRect.top;
        carousel.style.setProperty('--fp-nav-top', `${centerY}px`);
      };

      const runLayout = () => {
        positionNav();
        updateNavVisibility();
      };

      this.#layoutSchedulers.add(runLayout);
      this.#cleanups.push(() => this.#layoutSchedulers.delete(runLayout));

      if (prevBtn) {
        const onPrev = () => {
          viewport.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
        };
        prevBtn.addEventListener('click', onPrev);
        this.#cleanups.push(() => prevBtn.removeEventListener('click', onPrev));
      }

      if (nextBtn) {
        const onNext = () => {
          viewport.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
        };
        nextBtn.addEventListener('click', onNext);
        this.#cleanups.push(() => nextBtn.removeEventListener('click', onNext));
      }

      viewport.addEventListener('scroll', updateNavVisibility, { passive: true });
      this.#cleanups.push(() => viewport.removeEventListener('scroll', updateNavVisibility));

      const onResize = () => this.scheduleLayouts();
      window.addEventListener('resize', onResize, { passive: true });
      this.#cleanups.push(() => window.removeEventListener('resize', onResize));

      const desktopQuery = window.matchMedia('(min-width: 750px)');
      desktopQuery.addEventListener('change', onResize);
      this.#cleanups.push(() => desktopQuery.removeEventListener('change', onResize));

      if ('ResizeObserver' in window) {
        const grid = viewport.querySelector('.piece-fp__grid');
        const observer = new ResizeObserver(onResize);
        observer.observe(viewport);
        if (grid) observer.observe(grid);
        this.#cleanups.push(() => observer.disconnect());
      }

      viewport.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', onResize, { once: true });
        }
      });

      runLayout();
    });
  }

  initSwatches() {
    this.querySelectorAll('[data-fp-variant-data]').forEach((script) => {
      const card = script.closest('[data-fp-card]');
      if (!card) return;

      /** @type {Array<Record<string, unknown>>} */
      let variants;
      try {
        variants = JSON.parse(script.textContent || '[]');
      } catch {
        return;
      }

      const variantMap = new Map(
        variants.map((variant) => [String(variant.id), variant])
      );

      const refs = {
        productLink: card.querySelector('[data-fp-product-link]'),
        titleLink: card.querySelector('[data-fp-title-link]'),
        img: card.querySelector('.piece-fp__card-img'),
        vendor: card.querySelector('[data-fp-vendor]'),
        price: card.querySelector('[data-fp-price]'),
        compare: card.querySelector('[data-fp-compare]'),
        badge: card.querySelector('[data-fp-badge]'),
        saleBadge: card.querySelector('[data-fp-sale-badge]'),
        ratingWrap: card.querySelector('[data-fp-rating]'),
        ratingStars: card.querySelector('[data-fp-rating-stars]'),
        ratingCountEl: card.querySelector('[data-fp-rating-count]'),
      };

      const defaultProductUrl =
        refs.productLink instanceof HTMLAnchorElement ? refs.productLink.href : '';

      const swatches = [...card.querySelectorAll('[data-fp-swatch]')];
      const currentImageSrc =
        refs.img instanceof HTMLImageElement
          ? refs.img.currentSrc || refs.img.src
          : '';

      const preloadCardVariants = () => {
        PieceFeaturedProducts.preloadVariantImages(variants, currentImageSrc);
      };

      card.addEventListener('pointerenter', preloadCardVariants, { once: true, passive: true });
      card.addEventListener('focusin', preloadCardVariants, { once: true });
      this.#cleanups.push(() => {
        card.removeEventListener('pointerenter', preloadCardVariants);
        card.removeEventListener('focusin', preloadCardVariants);
      });

      /** @type {string | null} */
      let activeVariantId = card.getAttribute('data-fp-active-variant-id');

      /** @type {{ ratingValue: number; ratingCount: number } | null} */
      let activeRating = null;

      const applyVariant = (/** @type {Record<string, unknown>} */ variant) => {
        const variantId = String(variant.id || '');
        if (variantId && activeVariantId === variantId) return;

        activeVariantId = variantId;
        card.setAttribute('data-fp-active-variant-id', variantId);

        const title = String(variant.title || '');
        const url = PieceFeaturedProducts.resolveVariantUrl(variant, defaultProductUrl);
        const resolvedUrl = PieceFeaturedProducts.resolveUrl(url);

        if (refs.productLink instanceof HTMLAnchorElement) {
          if (PieceFeaturedProducts.resolveUrl(refs.productLink.href) !== resolvedUrl) {
            refs.productLink.href = url;
          }
          if (refs.productLink.getAttribute('aria-label') !== title) {
            refs.productLink.setAttribute('aria-label', title);
          }
        }

        if (refs.titleLink instanceof HTMLAnchorElement) {
          if (PieceFeaturedProducts.resolveUrl(refs.titleLink.href) !== resolvedUrl) {
            refs.titleLink.href = url;
          }
          if (refs.titleLink.textContent !== title) {
            refs.titleLink.textContent = title;
          }
        }

        if (refs.img instanceof HTMLImageElement && variant.imageSrc) {
          const nextSrc = String(variant.imageSrc);
          const nextSrcset = variant.imageSrcset ? String(variant.imageSrcset) : '';
          const currentSrc = PieceFeaturedProducts.resolveUrl(refs.img.currentSrc || refs.img.src);
          const resolvedNextSrc = PieceFeaturedProducts.resolveUrl(nextSrc);

          if (currentSrc !== resolvedNextSrc || refs.img.srcset !== nextSrcset) {
            refs.img.srcset = nextSrcset;
            refs.img.src = nextSrc;
            const nextAlt = String(variant.imageAlt || title);
            if (refs.img.alt !== nextAlt) refs.img.alt = nextAlt;
            if (variant.imageWidth) refs.img.width = Number(variant.imageWidth);
            if (variant.imageHeight) refs.img.height = Number(variant.imageHeight);
          }
        }

        if (refs.vendor instanceof HTMLElement) {
          const vendorText = String(variant.vendor || '');
          if (refs.vendor.textContent !== vendorText) {
            refs.vendor.textContent = vendorText;
          }
          refs.vendor.toggleAttribute('hidden', vendorText === '');
        }

        if (refs.price instanceof HTMLElement) {
          const priceText = String(variant.price || '');
          if (refs.price.textContent !== priceText) {
            refs.price.textContent = priceText;
          }
          refs.price.classList.toggle('piece-fp__card-price--sale', Boolean(variant.onSale));
        }

        if (refs.compare instanceof HTMLElement) {
          if (variant.onSale && variant.compareAtPrice) {
            const compareText = String(variant.compareAtPrice);
            if (refs.compare.textContent !== compareText) {
              refs.compare.textContent = compareText;
            }
            refs.compare.removeAttribute('hidden');
          } else {
            refs.compare.setAttribute('hidden', '');
          }
        }

        if (refs.badge instanceof HTMLElement) {
          refs.badge.toggleAttribute('hidden', !variant.showNewBadge);
        }

        if (refs.saleBadge instanceof HTMLElement) {
          const discountPercent = Number(variant.discountPercent ?? 0);
          const hasDiscount =
            Boolean(variant.onSale) && Number.isFinite(discountPercent) && discountPercent > 0;

          if (hasDiscount) {
            const badgeText = `-${discountPercent}%`;
            if (refs.saleBadge.textContent !== badgeText) {
              refs.saleBadge.textContent = badgeText;
            }
            refs.saleBadge.removeAttribute('hidden');
          } else {
            refs.saleBadge.setAttribute('hidden', '');
          }
        }

        if (refs.ratingWrap instanceof HTMLElement && refs.ratingStars instanceof HTMLElement) {
          const ratingValue = Number(variant.ratingValue ?? variant.rating ?? 0);
          const ratingCount = Number(variant.ratingCount ?? 0);
          const hasRating = Number.isFinite(ratingValue) && ratingValue > 0;
          const ratingChanged =
            activeRating == null ||
            activeRating.ratingValue !== ratingValue ||
            activeRating.ratingCount !== ratingCount;

          if (ratingChanged) {
            activeRating = { ratingValue, ratingCount };

            if (!hasRating) {
              if (refs.ratingStars.childElementCount > 0) {
                refs.ratingStars.replaceChildren();
              }
              refs.ratingWrap.removeAttribute('aria-label');
              refs.ratingWrap.setAttribute('aria-hidden', 'true');

              if (refs.ratingCountEl instanceof HTMLElement) {
                if (refs.ratingCountEl.textContent !== '') {
                  refs.ratingCountEl.textContent = '';
                }
                refs.ratingCountEl.setAttribute('hidden', '');
              }
            } else {
              refs.ratingWrap.removeAttribute('hidden');
              refs.ratingWrap.removeAttribute('aria-hidden');

              const ariaLabel = `${ratingValue} out of 5 stars${ratingCount > 0 ? `, ${ratingCount} reviews` : ''}`;
              if (refs.ratingWrap.getAttribute('aria-label') !== ariaLabel) {
                refs.ratingWrap.setAttribute('aria-label', ariaLabel);
              }

              refs.ratingStars.innerHTML = PieceFeaturedProducts.renderRatingStars(ratingValue);

              if (refs.ratingCountEl instanceof HTMLElement) {
                if (ratingCount > 0) {
                  const countText = `${ratingCount} đánh giá`;
                  if (refs.ratingCountEl.textContent !== countText) {
                    refs.ratingCountEl.textContent = countText;
                  }
                  refs.ratingCountEl.removeAttribute('hidden');
                } else {
                  if (refs.ratingCountEl.textContent !== '') {
                    refs.ratingCountEl.textContent = '';
                  }
                  refs.ratingCountEl.setAttribute('hidden', '');
                }
              }
            }
          }
        }
      };

      swatches.forEach((button) => {
        const preloadOnIntent = () => {
          const variantId = button.getAttribute('data-fp-variant-id');
          if (!variantId) return;

          const variant = variantMap.get(variantId);
          if (variant) PieceFeaturedProducts.queueVariantImagePreload(variant);
        };

        button.addEventListener('pointerenter', preloadOnIntent, { passive: true });
        button.addEventListener('focus', preloadOnIntent);
        this.#cleanups.push(() => button.removeEventListener('pointerenter', preloadOnIntent));
        this.#cleanups.push(() => button.removeEventListener('focus', preloadOnIntent));

        const onClick = () => {
          if (button.classList.contains('is-active')) return;

          const variantId = button.getAttribute('data-fp-variant-id');
          if (!variantId) return;

          const variant = variantMap.get(variantId);
          if (!variant) return;

          PieceFeaturedProducts.queueVariantImagePreload(variant);

          swatches.forEach((swatch) => {
            const isActive = swatch === button;
            swatch.classList.toggle('is-active', isActive);
            swatch.toggleAttribute('aria-current', isActive);
          });

          applyVariant(variant);
        };

        button.addEventListener('click', onClick);
        this.#cleanups.push(() => button.removeEventListener('click', onClick));
      });
    });
  }

  static resolveUrl(src) {
    try {
      return new URL(src, window.location.href).href;
    } catch {
      return src;
    }
  }

  static resolveVariantUrl(variant, fallbackUrl = '') {
    const raw = variant?.url != null ? String(variant.url).trim() : '';
    if (raw && raw !== '#' && raw !== 'null' && raw !== 'undefined') {
      return raw;
    }
    return fallbackUrl || '#';
  }

  static prefetchDocument(href) {
    const url = PieceFeaturedProducts.resolveUrl(href);
    if (!url || url === '#' || PieceFeaturedProducts.prefetchedDocumentUrls.has(url)) return;

    PieceFeaturedProducts.prefetchedDocumentUrls.add(url);

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
  }

  static queueVariantImagePreload(variant) {
    if (!variant?.imageSrc) return;

    const src = PieceFeaturedProducts.resolveUrl(String(variant.imageSrc));
    if (PieceFeaturedProducts.preloadedImageUrls.has(src)) return;

    PieceFeaturedProducts.preloadedImageUrls.add(src);

    const img = new Image();
    if (variant.imageSrcset) img.srcset = String(variant.imageSrcset);
    img.src = String(variant.imageSrc);
  }

  static preloadVariantImages(variants, excludeSrc = '') {
    const excluded = excludeSrc ? PieceFeaturedProducts.resolveUrl(excludeSrc) : '';

    variants.forEach((variant) => {
      if (!variant.imageSrc) return;

      const src = PieceFeaturedProducts.resolveUrl(String(variant.imageSrc));
      if (src === excluded) return;

      PieceFeaturedProducts.queueVariantImagePreload(variant);
    });
  }

  readCardGap() {
    const gapProperty =
      this.dataset.layout === 'carousel' ? '--fp-carousel-gap' : '--fp-card-gap';
    const gapValue = getComputedStyle(this).getPropertyValue(gapProperty).trim();
    const parsed = Number.parseFloat(gapValue);
    return Number.isFinite(parsed) ? parsed : 12;
  }

  static renderRatingStars(ratingValue) {
    const stars = Math.round(Number(ratingValue) * 2);

    return Array.from({ length: 5 }, (_, index) => {
      const starIndex = (index + 1) * 2;
      let starClass = 'piece-fp__star';

      if (stars >= starIndex) {
        starClass += ' piece-fp__star--full';
      } else if (stars === starIndex - 1) {
        starClass += ' piece-fp__star--half';
      }

      return `<span class="${starClass}" aria-hidden="true"></span>`;
    }).join('');
  }

  readColumnCount() {
    const styles = getComputedStyle(this);
    const mobile = Number.parseInt(styles.getPropertyValue('--fp-columns-mobile'), 10) || 2;
    const desktop = Number.parseInt(styles.getPropertyValue('--fp-columns-desktop'), 10) || 5;
    return window.matchMedia('(min-width: 750px)').matches ? desktop : mobile;
  }
}

const PIECE_FP_ELEMENT = 'piece-featured-products';

if (!customElements.get(PIECE_FP_ELEMENT)) {
  customElements.define(PIECE_FP_ELEMENT, PieceFeaturedProducts);
}
